import { Row, Col } from "antd";
import InputFilesDisabled from "components/panels/Job/InputFilesDisabled";
import InfoPanelDisabled from "components/panels/Job/InfoPanelDisabled";
import ParametersDisabled from "components/panels/Job/ParametersDisabled";
import ConstraintsDisabled from "components/panels/Job/ConstraintsDisabled";
import ButtonVelocity from "components/assets/utilities/ButtonVelocity";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DeleteJobAction } from "redux/actions/DeleteJob";
import { LoadJobAction } from "redux/actions/LoadJob";
import { LoadResultAction } from "redux/actions/LoadResult";
import { useAppDispatch, useAppSelector, useIsReadOnly } from "redux/hooks";
import { requestCompleted, requestFailure, requestStart, startSubmitting } from "redux/slices/app";
import { setModelIndex, setJobIndex } from "redux/slices/user";
import { useSession } from "components/wrappers/SessionProvider";
import Field from "redux/classes/Field";
import { JobPageWrapper } from "./styles";

const JobPage = () => {
  const dispatch = useAppDispatch();
  const app = useAppSelector(state => state.app);
  const user = useAppSelector(state => state.user);
  const pageLocation = useLocation().pathname;
  const params = useParams();
  const navigate = useNavigate();
  const loadJob = useRef(false);
  const session = useSession();
  const isReadOnly = useIsReadOnly();

  // See ResultPage for the rationale: hard reloads land on this URL with
  // modelIndex/jobIndex still at -1, so the page would crash on
  // user.jobs[-1].status. Sync indexes from URL params after Auth's
  // InitializeSession has populated the lists.
  useEffect(() => {
    if (user.models.length > 0 && user.modelIndex === -1 && params.modelName) {
      const m = user.models.find((mm: any) => mm.alias === params.modelName);
      if (m) dispatch(setModelIndex({ modelID: m.modelID }));
    }
    if (user.jobs.length > 0 && user.jobIndex === -1 && params.jobName) {
      dispatch(setJobIndex({ jobID: params.jobName }));
    }
  }, [user.models.length, user.jobs.length, user.modelIndex, user.jobIndex, params.modelName, params.jobName, dispatch])

  const modelInfo = user.models[user.modelIndex];
  const jobInfo = user.jobs[user.jobIndex];

  useEffect(() => {
    if (!modelInfo || !jobInfo) return;
    dispatch(LoadJobAction(session, jobInfo.jobID, modelInfo.alias))
    loadJob.current = true;
  }, [modelInfo?.alias, jobInfo?.jobID])

  useEffect(() => {
    if (loadJob.current && modelInfo && jobInfo) {
      if (jobInfo.status !== "RUNNING") {
        const getOutput = async () => {
          await dispatch(LoadResultAction(session, jobInfo.jobID, modelInfo.alias, user.config.outputs, user.jobs[user.jobIndex].status))
          dispatch(requestCompleted());
        }
        getOutput().catch(() => dispatch(requestFailure()));
      }
      else {
        dispatch(requestCompleted());
      }
    }
  }, [user.config])

  if (!modelInfo || !jobInfo) {
    return <div className="loader-centered"/>
  }

  return (
    <JobPageWrapper>
      {app.loading ? <div className="loader-centered"/> :
        <>
          <Row gutter={20} className="mt-24">
            <Col md={24} className="input-container">
              <InfoPanelDisabled
                values={user.config ? user.config.meta : {}}
              />
            </Col>
          </Row>

          {modelInfo.inputs.length > 0 &&
            <Row>
              <Col md={24}>
                <InputFilesDisabled
                  values={user.config ? user.config.inputs : []}
                />
              </Col>
            </Row>
          }

          {modelInfo.parameters.length > 0 &&
            <Row gutter={20} className="mt-24">
              <Col md={24} className="input-container">
                {modelInfo.parameters.length > 0 &&
                  <ParametersDisabled
                    values={user.config ? user.config.parameters : []}
                  />
                }
              </Col>
            </Row>
          }

          {modelInfo.parameters.filter((field: Field) => field.fieldType === "dropdowns").length > 0 &&
            <Row gutter={20} className="mt-24">
              <Col md={24} className="input-container">
                <ConstraintsDisabled
                  values={user.config ? user.config.parameters : []}
                />
              </Col>
            </Row>
          }

          <div className="job-footer">
            <ButtonVelocity
              type="delete"
              title={isReadOnly ? "Read-only — exit Admin View to delete" : (jobInfo.status === "RUNNING" ? "Kill this Job" : "Delete this Job (associated files will not be deleted from S3)")}
              path={pageLocation}
              disable={isReadOnly}
              onClick={async () => {
                if (isReadOnly) return;
                dispatch(requestStart());
                dispatch(startSubmitting());
                await dispatch(DeleteJobAction(session, jobInfo.jobID, jobInfo.taskID));
                navigate(`/${modelInfo.alias}/info`)
              }}
              submitting={app.submitting}
            >
              Delete Job
            </ButtonVelocity>
          </div>
        </>
      }
    </JobPageWrapper>
  );
};

export default JobPage;
